import React from 'react';
import { styled } from '@elliemae/ds-system';
import { SingleMenuItem, MultiMenuItem, Separator } from '@elliemae/ds-menu-items';

import { MENU_OPTION_TYPES } from '../constants.js';

import { ComboboxDataTestid } from '../ComboboxDataTestids.js';
import type { DSComboboxT } from '../react-desc-prop-types.js';
import { isSelected } from '../utils/listHelper.js';
import { DSFormComboboxName, FORM_COMBOBOX_SLOTS } from '../constants/index.js';

interface SectionProps {
  label: string;
  wrapperStyles: React.CSSProperties;
  innerRef: (instance: HTMLDivElement | null) => void;
  options: DSComboboxT.ItemOption[] | DSComboboxT.ItemSeparatorOptions[];
  handleClick: (option: DSComboboxT.ItemOption, e: React.MouseEvent<HTMLInputElement>) => void;
  handleOnMouseDown: (e: React.MouseEvent<HTMLLIElement>) => void;
  focusOptionIdx: string;
  selectedValues: DSComboboxT.SelectedOptionsT;
  getOwnerProps: () => object;
  getOwnerPropsArguments?: () => { index: number };
}

const StyledSectionWrapper = styled('section', { name: DSFormComboboxName, slot: FORM_COMBOBOX_SLOTS.SECTION_WRAPPER })`
  list-style: none;
  min-height: 24px;
  display: grid;
  align-items: center;
`;

const StyledSectionLabel = styled('header', { name: DSFormComboboxName, slot: FORM_COMBOBOX_SLOTS.SECTION_LABEL })`
  display: flex;
  align-items: center;
  padding: 0px 16px;
  min-height: 24px;
  font-size: 1rem;
  color: neutral-500;
`;

const StyledSingleMenuItem = styled(SingleMenuItem, {
  name: DSFormComboboxName,
  slot: FORM_COMBOBOX_SLOTS.SINGLE_MENU_ITEM,
})``;

export const Section = React.memo((props: SectionProps) => {
  const {
    label,
    wrapperStyles,
    focusOptionIdx,
    selectedValues,
    innerRef,
    handleOnMouseDown,
    options,
    handleClick,
    getOwnerProps,
    getOwnerPropsArguments,
  } = props;
  const id = `${label.replace(/ /g, '')}-section`;
  const CBItem = Array.isArray(selectedValues) ? MultiMenuItem : StyledSingleMenuItem;

  return (
    <StyledSectionWrapper
      role="group"
      aria-labelledby={id}
      style={wrapperStyles}
      innerRef={innerRef}
      getOwnerProps={getOwnerProps}
      getOwnerPropsArguments={getOwnerPropsArguments}
    >
      <StyledSectionLabel
        id={id}
        role="presentation"
        getOwnerProps={getOwnerProps}
        getOwnerPropsArguments={getOwnerPropsArguments}
      >
        {label}
      </StyledSectionLabel>
      {options?.map((option) => {
        const {
          dsId,
          type,
          value,
          label: optionLabel,
          readOnly,
          disabled,
          applyAriaDisabled,
          render,
          ...rest
        } = option;

        if (type === MENU_OPTION_TYPES.SEPARATOR) {
          return <Separator key={dsId} />;
        }

        return (
          <CBItem
            getOwnerProps={getOwnerProps}
            getOwnerPropsArguments={getOwnerPropsArguments}
            value={value}
            label={optionLabel}
            dataTestid={ComboboxDataTestid.OPTION}
            disabled={disabled}
            applyAriaDisabled={applyAriaDisabled || readOnly}
            onClick={(e: React.MouseEvent<HTMLInputElement>) => {
              if (applyAriaDisabled) return;
              handleClick(option, e);
            }}
            onMouseDown={handleOnMouseDown}
            isActive={dsId === focusOptionIdx}
            isSelected={isSelected(selectedValues, option)}
            tabIndex={-1}
            {...rest}
            key={option.dsId}
            dsId={option.dsId}
          />
        );
      })}
    </StyledSectionWrapper>
  );
});
